// First, before either session recorder: moves a bearer token out of the address bar. See
// `modules/pixwel/link-token.js`.
import './modules/pixwel/hide-link-token';
import React from 'react';
import './modules/pixwel/logrocket-setup';
import './modules/pixwel/aws-rum-setup';
import ReactModal from 'react-modal';
import Aspera from './modules/services/aspera';
import FlagService from './modules/services/flag-service';
import setupVideoJs from './modules/pixwel/videojs.setup';

import PixwelApp from './app';
import './modules/pixwel/styles';
import { handleGlobalError, SKIP_STATUSES } from './modules/pixwel/exception-handler.factory';
import { SENTRY_NETWORK_DENY_URLS } from './modules/pixwel/replay-privacy';
import { scrubBreadcrumb, scrubReplayEvent, scrubSentryEvent, scrubUrl } from './modules/pixwel/link-token';
import * as Sentry from '@sentry/react';
import { createRoot } from 'react-dom/client';

FlagService.init();
Sentry.init({
	dsn: 'https://e16ffaef16fb4debb8c9eed6d77843fe@o74502.ingest.sentry.io/5218200',
	environment: window.pixwel?.env,
	release: window.pixwel?.version,
	enabled: window.pixwel?.env !== 'development',
	integrations: [
		Sentry.browserTracingIntegration(),
		Sentry.replayIntegration({
			maskAllText: false,
			blockAllMedia: false,
			networkDetailAllowUrls: [
				'pixwel.com'
			],
			// The allow list above records request and response bodies for our own API, which
			// includes the authenticator seed, the recovery codes and the session token. Those
			// paths are denied; see `replay-privacy.js`.
			networkDetailDenyUrls: SENTRY_NETWORK_DENY_URLS,
			// A URL captured before `hide-link-token` rewrote it -- the navigation timing entry,
			// chiefly -- is scrubbed on its way into the buffer. See `link-token.js`.
			beforeAddRecordingEvent: scrubReplayEvent
		}),
		Sentry.feedbackIntegration({
			// Additional SDK configuration goes in here, for example:
			colorScheme: 'system'
		})
	],

	// Set tracesSampleRate to 1.0 to capture 100%
	// of transactions for performance monitoring.
	tracesSampleRate: 1.0,

	// Capture Replay for 10% of all sessions,
	// plus for 100% of sessions with an error
	replaysSessionSampleRate: 0,
	replaysOnErrorSampleRate: 1.0,

	// CefSharp bridge errors from embedded browsers and email link scanners
	// (e.g. Outlook Safe Links) — third-party noise, not app errors.
	ignoreErrors: [/Object Not Found Matching Id:\d+/],

	// Filter out expected HTTP errors and third-party noise from Sentry
	// Navigation breadcrumbs name the URL moved from and to; a token that reached one is
	// scrubbed. See `link-token.js`.
	beforeBreadcrumb(crumb) {
		return scrubBreadcrumb(crumb);
	},

	beforeSend(event, hint) {
		scrubSentryEvent(event);

		// Fingerprint Axios errors by HTTP method + URL path so that different
		// failing endpoints create separate Sentry issues instead of being
		// grouped under a single generic "Request failed with status code …".
		const axiosError = hint?.originalException;
		const axiosConfig = axiosError?.config || axiosError?.response?.config;
		if(axiosConfig?.url && axiosError?.response?.status) {
			const method = (axiosConfig.method || 'GET').toUpperCase();
			// Scrubbed before anything is derived from it: the request URL on the event is
			// redacted above, and a fingerprint built from the raw one would carry the
			// approval-action token to Sentry by another door. See `link-token.js`.
			const requestUrl = scrubUrl(axiosConfig.url);
			// Strip IDs (MongoID hex, UUIDs, numeric) from the path to group
			// e.g. /api/projects/507f1f77bcf86cd799439011 → /api/projects/:id
			let urlPath;
			try {
				const parsed = new URL(requestUrl, axiosConfig.baseURL || window.location.origin);
				urlPath = parsed.pathname;
			}
			catch{
				urlPath = requestUrl.split('?')[0];
			}
			const normalizedPath = urlPath.replace(
				/\/(?:[a-f0-9]{24}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|\d+)(?=\/|$)/gi,
				'/:id'
			);
			event.fingerprint = [method, String(axiosError.response.status), normalizedPath];
		}

		const error = hint?.originalException;
		if(error) {
			// Skip expected API response errors.
			// Check the error itself, and also unwrap PromiseRejectionEvent.reason
			// (onunhandledrejection may wrap the original error in an Event).
			const unwrapped = error?.reason || error;
			const status = unwrapped.status || unwrapped.response?.status;
			if(SKIP_STATUSES.includes(status)) {
				return null;
			}

			// Skip errors with invalid_token message from backend
			const message = unwrapped.message || unwrapped.error_description || '';
			if(typeof message === 'string' && /invalid(?:[ _]access)?[ _]token/i.test(message)) {
				return null;
			}

			// Skip transient network errors (timeouts, connection failures)
			if(unwrapped?.code === 'ECONNABORTED' || unwrapped?.message === 'Network Error'
				|| (typeof unwrapped?.message === 'string' && unwrapped.message.includes('timeout'))) {
				return null;
			}

			// Skip stale chunk errors after deployments (HTML served instead of JS)
			if(unwrapped instanceof SyntaxError && typeof unwrapped?.message === 'string'
				&& unwrapped.message.includes('Unexpected token \'<\'')) {
				return null;
			}

			// Skip CustomEvent unhandled rejections from third-party scripts/browser extensions
			const isCustomEvent = typeof CustomEvent === 'function' && error instanceof CustomEvent;
			if(isCustomEvent || (error?.type === 'unhandledrejection' && error?.isTrusted === false)) {
				return null;
			}
		}

		// Fallback: check the Sentry event's exception values for HTTP status codes
		// in case hint.originalException was not available or not an Axios error
		const exceptionValues = event?.exception?.values;
		if(exceptionValues?.length) {
			const exValue = exceptionValues[0]?.value || '';
			const statusMatch = typeof exValue === 'string' && exValue.match(/status code (\d+)/);
			if(statusMatch && SKIP_STATUSES.includes(Number(statusMatch[1]))) {
				return null;
			}
		}

		return event;
	}
});

try {
	const container = document.getElementById('root');
	const root = createRoot(container); // createRoot(container!) if you use TypeScript
	root.render(<PixwelApp/>);

	ReactModal.setAppElement('#root');
	setupVideoJs();
	Aspera.init();
}
catch(e) {
	handleGlobalError(e);
}

// Populate window.pixwel from the API: its baked-in git SHA, and the public client ID of each
// sign-in provider, which are configured on the API and never duplicated into this bundle.
//
// The promise is kept as `configReady` because the app above has already rendered by now:
// anything that needs a value from here has to wait for it rather than read a field that is
// not filled in yet. It resolves either way — a failed system-check leaves the fields null,
// which reads as "no Google sign-in" and nothing else.
window.pixwel.configReady = fetch(`${window.apiBase}/api/system-check`, { cache: 'no-store' })
	.then((r) => r.json())
	.then((data) => {
		if(window.pixwel) {
			window.pixwel.apiVersion = data.version || null;
			window.pixwel.googleClientId = data.googleClientId || null;
			window.pixwel.microsoftClientId = data.microsoftClientId || null;
			// A boolean rather than an id: unlike an OAuth client, the browser never needs to
			// know the relying party — the ceremony options carry it, and they come from the API.
			window.pixwel.passkeys = !!data.passkeys;
		}
	})
	.catch(() => { /* non-fatal */ });
